import React from 'react';
import { Beaker, ArrowRightIcon, XIcon, CheckIcon } from 'lucide-react';
export const ScientificApproach = () => {
  return <section className="my-12" data-id="element-3813">
      <h2 className="text-2xl font-serif font-bold text-slate-900 mt-10 mb-5 leading-tight border-b border-slate-200 pb-2 flex items-center" data-id="element-3814">
        <Beaker className="h-5 w-5 text-slate-700 mr-2 inline" data-id="element-3815" />
        Why Our Approach Works When Others Don't
      </h2>
      <p className="text-base text-slate-800 leading-relaxed mb-8" data-id="element-3816">
        Our scientific approach targets the root causes of women's health
        issues, not just the symptoms. This fundamental difference explains why
        our solutions succeed where others have failed.
      </p>
      {/* Visual Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3817">
        <div className="bg-white p-5 border border-slate-200 rounded-lg" data-id="element-3818">
          <div className="flex items-center mb-4" data-id="element-3819">
            <XIcon className="h-5 w-5 text-rose-500 mr-2" data-id="element-3820" />
            <h3 className="text-lg font-serif font-semibold text-slate-900" data-id="element-3821">
              Standard Approaches
            </h3>
          </div>
          <ul className="space-y-3" data-id="element-3822">
            <li className="flex items-start" data-id="element-3823">
              <XIcon className="h-4 w-4 text-rose-500 mt-1 mr-2 flex-shrink-0" data-id="element-3824" />
              <span className="text-slate-700" data-id="element-3825">
                Target symptoms instead of underlying causes
              </span>
            </li>
            <li className="flex items-start" data-id="element-3826">
              <XIcon className="h-4 w-4 text-rose-500 mt-1 mr-2 flex-shrink-0" data-id="element-3827" />
              <span className="text-slate-700" data-id="element-3828">
                Ignore protective biofilms that shield bacteria
              </span>
            </li>
            <li className="flex items-start" data-id="element-3829">
              <XIcon className="h-4 w-4 text-rose-500 mt-1 mr-2 flex-shrink-0" data-id="element-3830" />
              <span className="text-slate-700" data-id="element-3831">
                Use generic probiotics that don't survive stomach acid
              </span>
            </li>
            <li className="flex items-start" data-id="element-3832">
              <XIcon className="h-4 w-4 text-rose-500 mt-1 mr-2 flex-shrink-0" data-id="element-3833" />
              <span className="text-slate-700" data-id="element-3834">
                Temporarily improve conditions but allow recurrence
              </span>
            </li>
            <li className="flex items-start" data-id="element-3835">
              <XIcon className="h-4 w-4 text-rose-500 mt-1 mr-2 flex-shrink-0" data-id="element-3836" />
              <span className="text-slate-700" data-id="element-3837">
                Based on marketing claims rather than clinical evidence
              </span>
            </li>
          </ul>
          <div className="mt-4 pt-3 border-t border-slate-100 text-center" data-id="element-3838">
            <span className="inline-block bg-rose-50 text-rose-700 text-sm px-3 py-1 rounded-full" data-id="element-3839">
              Result: Temporary relief, recurring problems
            </span>
          </div>
        </div>
        <div className="bg-white p-5 border-2 border-green-200 rounded-lg relative" data-id="element-3840">
          <div className="absolute -top-3 -right-3 bg-green-100 text-green-800 text-xs px-2 py-1 rounded border border-green-200" data-id="element-3841">
            Our Approach
          </div>
          <div className="flex items-center mb-4" data-id="element-3842">
            <CheckIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3843" />
            <h3 className="text-lg font-serif font-semibold text-slate-900" data-id="element-3844">
              Our Scientific Method
            </h3>
          </div>
          <ul className="space-y-3" data-id="element-3845">
            <li className="flex items-start" data-id="element-3846">
              <CheckIcon className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-3847" />
              <span className="text-slate-700" data-id="element-3848">
                Targets root causes with precision-engineered solutions
              </span>
            </li>
            <li className="flex items-start" data-id="element-3849">
              <CheckIcon className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-3850" />
              <span className="text-slate-700 bg-yellow-100 px-1" data-id="element-3851">
                Uses enzymes to break down protective bacterial biofilms
              </span>
            </li>
            <li className="flex items-start" data-id="element-3852">
              <CheckIcon className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-3853" />
              <span className="text-slate-700" data-id="element-3854">
                Employs spore-forming probiotics that survive digestion
              </span>
            </li>
            <li className="flex items-start" data-id="element-3855">
              <CheckIcon className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-3856" />
              <span className="text-slate-700" data-id="element-3857">
                Creates lasting changes to prevent recurrence
              </span>
            </li>
            <li className="flex items-start" data-id="element-3858">
              <CheckIcon className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" data-id="element-3859" />
              <span className="text-slate-700" data-id="element-3860">
                Validated through rigorous clinical trials and peer review
              </span>
            </li>
          </ul>
          <div className="mt-4 pt-3 border-t border-slate-100 text-center" data-id="element-3861">
            <span className="inline-block bg-green-50 text-green-700 text-sm px-3 py-1 rounded-full" data-id="element-3862">
              Result: 89% remain symptom-free after 6 months
            </span>
          </div>
        </div>
      </div>
      {/* Key Differentiators */}
      <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-8" data-id="element-3863">
        <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4" data-id="element-3864">
          Our Key Differentiators
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3865">
          <div className="bg-white p-4 rounded border border-slate-200" data-id="element-3866">
            <h4 className="font-medium text-slate-900 flex items-center text-sm" data-id="element-3867">
              <ArrowRightIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-3868" />
              Biofilm-Disruption Technology
            </h4>
            <p className="text-sm text-slate-600 mt-1" data-id="element-3869">
              Our proprietary enzyme complex breaks down the protective shield
              that bacteria hide behind, allowing treatments to finally reach
              their target.
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-slate-200" data-id="element-3870">
            <h4 className="font-medium text-slate-900 flex items-center text-sm" data-id="element-3871">
              <ArrowRightIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-3872" />
              Spore-Forming Probiotics
            </h4>
            <p className="text-sm text-slate-600 mt-1" data-id="element-3873">
              Unlike standard probiotics that die in stomach acid, our strains
              create protective shells to ensure they reach their destination
              alive and active.
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-slate-200" data-id="element-3874">
            <h4 className="font-medium text-slate-900 flex items-center text-sm" data-id="element-3875">
              <ArrowRightIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-3876" />
              Precision Delivery Systems
            </h4>
            <p className="text-sm text-slate-600 mt-1" data-id="element-3877">
              Our gummy formulations protect active ingredients and ensure
              optimal release timing for maximum effectiveness.
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-slate-200" data-id="element-3878">
            <h4 className="font-medium text-slate-900 flex items-center text-sm" data-id="element-3879">
              <ArrowRightIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-3880" />
              Triple-Fix Methodology
            </h4>
            <p className="text-sm text-slate-600 mt-1" data-id="element-3881">
              Our solutions address three critical factors simultaneously:
              breaking protective barriers, restoring balance, and preventing
              recurrence.
            </p>
          </div>
        </div>
      </div>
      {/* Quality Standards */}
      <div className="bg-white p-5 border border-slate-200 rounded-lg" data-id="element-3882">
        <h3 className="text-lg font-serif font-semibold text-slate-900 mb-3" data-id="element-3883">
          Our Quality Standards
        </h3>
        <ul className="space-y-2" data-id="element-3884">
          <li className="flex items-start" data-id="element-3885">
            <span className="text-amber-600 font-semibold mr-2" data-id="element-3886">•</span>
            <span className="text-slate-700" data-id="element-3887">
              <span className="font-medium" data-id="element-3888">Triple-Testing Protocol:</span>{' '}
              Every batch undergoes 3 independent laboratory tests for potency
              and purity
            </span>
          </li>
          <li className="flex items-start" data-id="element-3889">
            <span className="text-amber-600 font-semibold mr-2" data-id="element-3890">•</span>
            <span className="text-slate-700" data-id="element-3891">
              <span className="font-medium" data-id="element-3892">cGMP Certified Facilities:</span>{' '}
              All products manufactured in FDA-registered, cGMP-certified
              facilities
            </span>
          </li>
          <li className="flex items-start" data-id="element-3893">
            <span className="text-amber-600 font-semibold mr-2" data-id="element-3894">•</span>
            <span className="text-slate-700" data-id="element-3895">
              <span className="font-medium" data-id="element-3896">Independent Verification:</span>{' '}
              Third-party testing confirms bioactivity of key enzymes and
              probiotic viability
            </span>
          </li>
          <li className="flex items-start" data-id="element-3897">
            <span className="text-amber-600 font-semibold mr-2" data-id="element-3898">•</span>
            <span className="text-slate-700" data-id="element-3899">
              <span className="font-medium" data-id="element-3900">Transparency Commitment:</span> All
              research methodologies and test results available for review
            </span>
          </li>
        </ul>
      </div>
    </section>;
};